'use client';

import React, { FC, ReactNode, ReactElement, useState, useEffect, useRef } from 'react';
import { FiCopy, FiCheck, FiTerminal } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'next-themes';
import hljs from 'highlight.js';

// Styles moved here for centralized control
import 'highlight.js/styles/github.css'; // Light Theme
import 'highlight.js/styles/atom-one-dark.css'; // Dark Theme

interface CodeBlockProps {
  children: ReactNode;
  language: string;
}

const CodeBlock: FC<CodeBlockProps> = ({ children, language }) => {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  const codeRef = useRef<HTMLElement>(null);

  // 1. Highlight.js Trigger
  useEffect(() => {
    if (codeRef.current) {
      // Remove data-highlighted to allow re-highlighting on theme switch
      codeRef.current.removeAttribute('data-highlighted');
      hljs.highlightElement(codeRef.current);
    }
  }, [resolvedTheme, children, language]);

  // 2. Text Extraction Logic
  const extractText = (node: ReactNode): string => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(extractText).join('');
    if (React.isValidElement(node)) {
      const element = node as ReactElement<{ children?: ReactNode }>;
      return extractText(element.props.children);
    }
    return '';
  };

  const copyToClipboard = () => {
    const textToCopy = extractText(children);
    if (textToCopy.trim()) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div className="group relative my-8 rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-[#282c34] shadow-sm transition-colors duration-300">
      {/* HEADER BAR */}
      <div className="flex justify-between items-center bg-zinc-50 dark:bg-zinc-900/50 px-4 py-2 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-2">
          <FiTerminal className="text-indigo-500" size={12} />
          <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500">
            {language || 'src'}
          </span>
        </div>
        
        <button
          onClick={copyToClipboard}
          className="flex items-center gap-2 text-[10px] font-mono font-bold uppercase tracking-tighter text-zinc-500 hover:text-indigo-500 transition-colors"
        >
          <AnimatePresence mode="wait">
            {copied ? (
              <motion.div
                key="check"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="flex items-center gap-1.5 text-indigo-500"
              >
                <FiCheck size={14} />
                <span>Copied</span>
              </motion.div>
            ) : (
              <motion.div
                key="copy"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1.5"
              >
                <FiCopy size={14} />
                <span>Copy</span>
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* CODE AREA */}
      <div className="relative">
        <pre className="p-4 overflow-auto custom-scrollbar bg-transparent text-sm leading-relaxed">
          <code 
            ref={codeRef}
            className={`language-${language} font-mono block`}
          >
            {children}
          </code>
        </pre>
        
        {/* Subtle Fade-out for Overflow */}
        <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white dark:from-[#282c34] to-transparent pointer-events-none opacity-40" />
      </div>

      {/* FOOTER DECORATION */}
      <div className="h-[2px] w-full bg-zinc-100 dark:bg-zinc-900">
        <motion.div 
          initial={{ width: 0 }}
          whileInView={{ width: '100%' }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "circOut" }}
          className="h-full bg-indigo-500/40"
        />
      </div>
    </div>
  );
};

export default CodeBlock;